import { first, last, uniqBy } from 'lodash';

import { FuelEvent } from './fuel-event';
import { AllocationEquipment } from './allocation-equipment';

export class AllocationSummary {
	private openingBalanceEvent: FuelEvent | null;
	private closingBalanceEvent: FuelEvent | null;

	private withdrawals: FuelEvent[];

	public get openingBalance(): string {
		return this.formatBalanceEvent(this.openingBalanceEvent);
	}

	public get closingBalance(): string {
		return this.formatBalanceEvent(this.closingBalanceEvent);
	}

	public totalA(totalFuelPurchased: number): number | null {
		if (!this.openingBalanceEvent) {
			return null;
		}
		return this.openingBalanceEvent.volume + totalFuelPurchased;
	}

	public totalB(ineligibleUse: number): number | null {
		if (!this.closingBalanceEvent) {
			return null;
		}
		return this.closingBalanceEvent.volume + ineligibleUse;
	}

	public totalC(totalFuelPurchased: number, ineligibleUse: number): string | null {
		const totalA = this.totalA(totalFuelPurchased);
		const totalB = this.totalB(ineligibleUse);
		if (!totalA || !totalB) {
			return null;
		}
		return `${totalA - totalB}L`;
	}

	public get equipmentAllocations(): {
		equipment: AllocationEquipment;
		litresAllocated: number;
	}[] {
		const allocationsWithEquipment = this.allocatedWithdrawals.filter(
			(withdrawal: FuelEvent) => withdrawal.allocation!.equipment != null,
		);
		const equipment = uniqBy(
			allocationsWithEquipment.map((withdrawal: FuelEvent) => withdrawal.allocation!.equipment!),
			(equipment: AllocationEquipment) => equipment.id,
		);
		return equipment
			.map((e: AllocationEquipment) => {
				const equipmentAllocations = allocationsWithEquipment.filter(
					(withdrawal: FuelEvent) => withdrawal.allocation!.equipment!.id === e.id,
				);
				return {
					equipment: e,
					litresAllocated: equipmentAllocations.reduce<number>(
						(litres: number, withdrawal: FuelEvent) => litres + withdrawal.volume,
						0,
					),
				};
			})
			.sort((a, b) => b.litresAllocated - a.litresAllocated);
	}

	public get unallocatedTotal(): string {
		const unallocatedLitres: number = this.unallocatedWithdrawals.reduce(
			(litres: number, withdrawal: FuelEvent) => litres + withdrawal.volume,
			0,
		);
		return `${unallocatedLitres}L`;
	}

	public get allocatedTotal(): string {
		const allocatedLitres: number = this.allocatedWithdrawals.reduce(
			(litres: number, withdrawal: FuelEvent) => litres + withdrawal.volume,
			0,
		);
		return `${allocatedLitres}L`;
	}

	constructor(fuelEvents: FuelEvent[]) {
		const tankBalances = fuelEvents.filter((event: FuelEvent) => event.type === 'Tank Balance');
		this.withdrawals = fuelEvents.filter((event: FuelEvent) => event.type === 'Withdrawal');
		this.openingBalanceEvent = first(tankBalances) ?? null;
		this.closingBalanceEvent = last(tankBalances) ?? null;
	}

	private get allocatedWithdrawals(): FuelEvent[] {
		return this.withdrawals.filter((withdrawal: FuelEvent) => withdrawal.allocation?.id != null);
	}

	private get unallocatedWithdrawals(): FuelEvent[] {
		return this.withdrawals.filter((withdrawal: FuelEvent) => withdrawal.allocation?.id == null);
	}

	private formatBalanceEvent(event: FuelEvent | null): string {
		if (event) {
			return `${event.volume}L`;
		}
		return 'Unknown';
	}
}
